import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Flex,
  LoadingOverlay,
  Modal,
  Text,
} from "@mantine/core";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";

import {
  columns,
  type ExistingUserRow,
  type UserManagementTableRow,
  validateDraftUser,
} from "./columns";
import { useUserContext } from "@/context/user.context";
import {
  useCreateUserMutation,
  useDeactivateUserMutation,
  useReInviteUserMutation,
  useUpdateCanSendPdfsMutation,
  useUpdateUserRoleMutation,
  useUsersManagementListQuery,
} from "@/hooks/queries/user.queries";
import {
  type DraftUser,
  type UserManagementListUser,
  type UserRole,
  UserStatus,
} from "@/types/user";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";
import { trimStringValues } from "@/utils/string";

const NOTIFICATION_MESSAGES = {
  // Company name should always be defined. If there is an edge case, fall back
  // to product name.
  inviteSuccess: (companyName = "Upwage") => `User invited to ${companyName}`,
  inviteError: "Failed to invite user. Please try again.",
  roleUpdateSuccess: "User's role updated",
  roleUpdateError: "Failed to update user role. Please try again.",
  deactivateSuccess: (companyName = "Upwage") =>
    `User removed from ${companyName}`,
  deactivateError: "Failed to remove user. Please try again.",
  reInviteSuccess: "User re-invited successfully",
  reInviteError: "Failed to re-invite user. Please try again.",
} as const;

interface UserManagementModalProps {
  onClose: () => void;
  opened: boolean;
}

const USERS_PLACEHOLDER: UserManagementListUser[] = [] as const;

export const UserManagementModal = ({
  onClose,
  opened,
}: UserManagementModalProps) => {
  const { data: users = USERS_PLACEHOLDER, isLoading: isLoadingUsers } =
    useUsersManagementListQuery();
  const { company, refreshUser, user: currentUser } = useUserContext();

  const [draftUsers, setDraftUsers] = useState<DraftUser[]>([]);
  const [pendingInviteIds, setPendingInviteIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingReInviteIds, setPendingReInviteIds] = useState<Set<string>>(
    new Set(),
  );

  const { mutate: createUser } = useCreateUserMutation();
  const { mutate: updateRole } = useUpdateUserRoleMutation();
  const { mutate: deactivateUser } = useDeactivateUserMutation();
  const { mutate: reInviteUser } = useReInviteUserMutation();
  const { mutate: updateCanSendPdfs } = useUpdateCanSendPdfsMutation();

  const addDraftUser = useCallback(() => {
    setDraftUsers((prev) => [
      {
        tempId: nanoid(),
        name: "",
        email: "",
        role: null,
        isDraft: true,
      },
      ...prev,
    ]);
  }, []);

  const updateDraftUser = useCallback(
    (
      tempId: string,
      updates: Partial<Pick<DraftUser, "name" | "email" | "role">>,
    ) => {
      setDraftUsers((prev) =>
        prev.map((draft) =>
          draft.tempId === tempId ? { ...draft, ...updates } : draft,
        ),
      );
    },
    [],
  );

  const removeDraftUser = useCallback((tempId: string) => {
    setDraftUsers((prev) => prev.filter((draft) => draft.tempId !== tempId));
  }, []);

  const handleClose = useCallback(() => {
    setDraftUsers([]);
    onClose();
  }, [onClose]);

  const handleInvite = useCallback(
    (draft: DraftUser) => {
      const validationResult = validateDraftUser(draft);
      if (!validationResult.isValid) {
        return;
      }

      setPendingInviteIds((prev) => new Set(prev).add(draft.tempId));

      const { draft: validDraft } = validationResult;
      createUser(
        trimStringValues({
          email: validDraft.email,
          fullName: validDraft.name,
          role: validDraft.role,
        }),
        {
          onSuccess: () => {
            setPendingInviteIds((prev) => {
              const next = new Set(prev);
              next.delete(draft.tempId);
              return next;
            });
            removeDraftUser(draft.tempId);
            showUpwageNotification(
              NOTIFICATION_MESSAGES.inviteSuccess(company?.name),
              NotificationTypes.Success,
            );
          },
          onError: () => {
            setPendingInviteIds((prev) => {
              const next = new Set(prev);
              next.delete(draft.tempId);
              return next;
            });
            showUpwageNotification(
              NOTIFICATION_MESSAGES.inviteError,
              NotificationTypes.Error,
            );
          },
        },
      );
    },
    [company?.name, createUser, removeDraftUser],
  );

  const handleRoleChange = useCallback(
    (userId: string, role: UserRole) => {
      updateRole(
        { userId, role },
        {
          onSuccess: () => {
            showUpwageNotification(
              NOTIFICATION_MESSAGES.roleUpdateSuccess,
              NotificationTypes.Success,
            );
          },
          onError: () => {
            showUpwageNotification(
              NOTIFICATION_MESSAGES.roleUpdateError,
              NotificationTypes.Error,
            );
          },
        },
      );
    },
    [updateRole],
  );

  const handleDeactivate = useCallback(
    (userId: string) => {
      deactivateUser(userId, {
        onSuccess: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.deactivateSuccess(company?.name),
            NotificationTypes.Success,
          );
        },
        onError: () => {
          showUpwageNotification(
            NOTIFICATION_MESSAGES.deactivateError,
            NotificationTypes.Error,
          );
        },
      });
    },
    [company?.name, deactivateUser],
  );

  const handleReInvite = useCallback(
    (userId: string) => {
      setPendingReInviteIds((prev) => new Set(prev).add(userId));

      reInviteUser(userId, {
        onSuccess: () => {
          setPendingReInviteIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
          showUpwageNotification(
            NOTIFICATION_MESSAGES.reInviteSuccess,
            NotificationTypes.Success,
          );
        },
        onError: () => {
          setPendingReInviteIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
          showUpwageNotification(
            NOTIFICATION_MESSAGES.reInviteError,
            NotificationTypes.Error,
          );
        },
      });
    },
    [reInviteUser],
  );

  const handleSendPdfsChange = useCallback(
    (userId: string, canSendCandidatePdfs: boolean) => {
      updateCanSendPdfs(
        { userId, canSendCandidatePdfs },
        {
          onSuccess: () => {
            if (userId === currentUser?.id) {
              refreshUser();
            }
            showUpwageNotification(
              canSendCandidatePdfs
                ? "PDF sharing enabled for user"
                : "PDF sharing disabled for user",
              NotificationTypes.Success,
            );
          },
          onError: () => {
            showUpwageNotification(
              "Failed to update PDF sharing permission. Please try again.",
              NotificationTypes.Error,
            );
          },
        },
      );
    },
    [currentUser?.id, refreshUser, updateCanSendPdfs],
  );

  const tableData: UserManagementTableRow[] = useMemo(() => {
    const existingUsers: ExistingUserRow[] = users.map((u) => ({
      ...u,
      isDraft: false as const,
    }));

    const activeUsers = existingUsers.filter(
      ({ status }) => status !== UserStatus.Inactive,
    );
    const inactiveUsers = existingUsers.filter(
      ({ status }) => status === UserStatus.Inactive,
    );

    return [...draftUsers, ...activeUsers, ...inactiveUsers];
  }, [draftUsers, users]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => (row.isDraft ? row.tempId : row.id),
    meta: {
      userManagement: {
        currentUserId: currentUser?.id,
        isInvitePending: (tempId: string) => pendingInviteIds.has(tempId),
        isReInvitePending: (userId: string) => pendingReInviteIds.has(userId),
        onDeactivate: handleDeactivate,
        onInvite: handleInvite,
        onReInvite: handleReInvite,
        onRemoveDraft: removeDraftUser,
        onRoleChange: handleRoleChange,
        onSendPdfsChange: handleSendPdfsChange,
        onUpdateDraft: updateDraftUser,
      },
    },
  });

  return (
    <Modal
      className="shadow-[0px_0px_24px_0px_rgba(0,0,0,0.1)] overflow-hidden"
      onClose={handleClose}
      opened={opened}
      padding={0}
      radius={6}
      size="auto"
      withCloseButton={false}
    >
      <LoadingOverlay visible={!company || isLoadingUsers} />

      <Flex
        className="border-b border-uw-gray-0 px-6 py-4"
        justify="space-between"
        align="center"
      >
        <Text fw={600} size="md" c="gray.9">
          {company?.name} users
        </Text>
        <Flex align="center" gap={24}>
          <Button
            className="h-10 px-4 gap-1 text-sm font-semibold"
            color="purple.5"
            onClick={addDraftUser}
            rightSection={<FontAwesomeIcon icon={faPlus} size="xs" />}
            radius={4}
          >
            Add user
          </Button>
          <ActionIcon onClick={handleClose} variant="transparent" size="sm">
            <FontAwesomeIcon
              className="bg-uw-gray-7 rounded-full text-uw-gray-0"
              icon={faCircleX}
            />
          </ActionIcon>
        </Flex>
      </Flex>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-uw-gray-0 bg-white"
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 first:pl-6 text-left uppercase"
                    style={{
                      width: header.getSize(),
                      minWidth: header.getSize(),
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b border-uw-gray-0 bg-white">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-6 py-3 first:pl-6"
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.getSize(),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Flex className="bg-white px-6 py-4">
        <Button
          className="h-8 px-4 text-sm font-semibold"
          onClick={handleClose}
          color="gray.0"
          c="gray.9"
          radius={4}
        >
          Close
        </Button>
      </Flex>
    </Modal>
  );
};
